import { useState } from 'react';
import { csatAverages, CSAT_DIMENSIONS, type CsatDimension } from '@qms360/shared';
import { DerivedFigure } from './DerivedFigure.js';
import { ApiError, addCsatResponse, archiveCsatResponse, type CsatResponse } from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * Customer satisfaction — six 1–5 rated dimensions per respondent (spec §9).
 *
 * Many respondents per period, and a **skipped question is absent, not a zero**.
 * Counting a blank as zero would punish a project for a question nobody answered,
 * so the averages come from the shared helper that shrinks only that dimension's
 * own denominator.
 */

const DIMENSION_LABELS: Record<CsatDimension, string> = {
  ratingQuality: 'Quality',
  ratingTimeliness: 'Timeliness',
  ratingCommunication: 'Communication',
  ratingResponsiveness: 'Responsiveness',
  ratingDocumentation: 'Documentation',
  ratingOverall: 'Overall satisfaction',
};

const EMPTY: Record<string, string> = {
  respondentName: '',
  ratingQuality: '',
  ratingTimeliness: '',
  ratingCommunication: '',
  ratingResponsiveness: '',
  ratingDocumentation: '',
  ratingOverall: '',
  feedback: '',
};

export function CustomerSatisfaction({
  periodId,
  responses,
  editable,
  onChanged,
}: {
  periodId: string;
  responses: CsatResponse[];
  editable: boolean;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // The SAME helper the server and the export use, so the screen cannot show an
  // average the report disagrees with (ADR-0007).
  const averages = csatAverages(
    responses.map((response) => ({
      ratingQuality: response.ratingQuality,
      ratingTimeliness: response.ratingTimeliness,
      ratingCommunication: response.ratingCommunication,
      ratingResponsiveness: response.ratingResponsiveness,
      ratingDocumentation: response.ratingDocumentation,
      ratingOverall: response.ratingOverall,
    })),
  );

  const save = async () => {
    setBusy(true);
    try {
      await addCsatResponse(periodId, {
        respondentName: draft.respondentName || null,
        // An empty box means "not answered", which is null — never 0.
        ...Object.fromEntries(
          CSAT_DIMENSIONS.map((dimension) => [
            dimension,
            draft[dimension] === '' ? null : Number(draft[dimension]),
          ]),
        ),
        outcomeAnswers: {},
        feedback: draft.feedback || null,
      });
      setDraft(EMPTY);
      setSaveError(null);
      onChanged();
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : t("The response could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-md border border-border bg-surface p-5 shadow-card">
      <h2 className="m-0 text-base font-semibold">{t("Customer Satisfaction")}</h2>
      <p className="mt-1 text-sm text-content-secondary">
        {responses.length} {responses.length === 1 ? t("response") : t("responses")}{t(". A question left blank is not counted as zero.")}
      </p>
      {saveError && (
        <p role="alert" className="mb-0 mt-2 text-sm text-tone-critical">
          {saveError}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-4 border-b border-border pb-4 sm:grid-cols-4 lg:grid-cols-7">
        {CSAT_DIMENSIONS.map((dimension) => (
          <DerivedFigure
            key={dimension}
            label={DIMENSION_LABELS[dimension]}
            value={averages.byDimension[dimension].value}
            explanation={
              averages.byDimension[dimension].value === null
                ? averages.byDimension[dimension].explanation
                : null
            }
          />
        ))}
        <DerivedFigure
          label={t("Average of all questions")}
          value={averages.overall.value}
          explanation={averages.overall.value === null ? averages.overall.explanation : null}
        />
      </dl>

      {responses.length > 0 && (
        <ul className="mt-4 list-none space-y-2 p-0">
          {responses.map((response) => (
            <li
              key={response.id}
              className="flex items-center justify-between gap-3 rounded-sm border border-border px-3 py-2 text-sm"
            >
              <span>{response.respondentName ?? t("Anonymous respondent")}</span>
              {editable && (
                <button
                  type="button"
                  onClick={async () => {
                    // Archives rather than deletes (Rule 2).
                    try {
                      await archiveCsatResponse(periodId, response.id);
                      setSaveError(null);
                      onChanged();
                    } catch (caught) {
                      setSaveError(caught instanceof ApiError ? caught.message : t("The response could not be archived."));
                    }
                  }}
                  className="rounded-sm border border-border-strong px-2 py-1 text-xs"
                >
                  {t("Archive")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="mt-5 border-t border-border pt-4">
          <h3 className="m-0 text-sm font-semibold">{t("Add a response")}</h3>

          <div className="mt-3">
            <label htmlFor="csat-name" className="text-xs font-medium text-content-secondary">
              {t("Respondent (optional)")}
            </label>
            <input
              id="csat-name"
              value={draft.respondentName}
              onChange={(event) => setDraft({ ...draft, respondentName: event.target.value })}
              className="mt-1 w-full max-w-sm rounded-sm border border-border-strong px-2 py-1.5 text-sm"
            />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {CSAT_DIMENSIONS.map((dimension) => (
              <div key={dimension}>
                <label
                  htmlFor={`csat-${dimension}`}
                  className="text-xs font-medium text-content-secondary"
                >
                  {t(DIMENSION_LABELS[dimension])}
                </label>
                <select
                  id={`csat-${dimension}`}
                  value={draft[dimension]}
                  onChange={(event) => setDraft({ ...draft, [dimension]: event.target.value })}
                  className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm"
                >
                  <option value="">{t("Not answered")}</option>
                  {[1, 2, 3, 4, 5].map((score) => (
                    <option key={score} value={score}>
                      {score}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <label htmlFor="csat-feedback" className="text-xs font-medium text-content-secondary">
              {t("Feedback (optional)")}
            </label>
            <textarea
              id="csat-feedback"
              rows={3}
              value={draft.feedback}
              onChange={(event) => setDraft({ ...draft, feedback: event.target.value })}
              className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm"
            />
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="mt-3 rounded-sm border border-ag-purple px-3 py-1.5 text-sm font-medium text-ag-purple disabled:opacity-60"
          >
            {t("Add response")}
          </button>
        </div>
      )}
    </section>
  );
}
